/**
 * gapDetect — locate gaps in an OHLCV bar array.
 *
 * Why this exists: the JournifiBars AddOn (or any bar source) occasionally
 * drops bars — write failures, NT being busy at a bar-close boundary, etc.
 * When a dropped bar straddles a live trade's entry timestamp, the
 * counter-signal detector silently picks the wrong reference bar and
 * fabricates a phantom whisper. counterSignals.js now has a defensive
 * containment guard that bails out in this case (no false signal), but
 * the trade still goes unmonitored until the missing bar finally arrives.
 *
 * This module enables the live polling layer to:
 *   1. SEE that a gap exists immediately after fetching bars
 *   2. DECIDE whether to retry (cache-bust) right away or wait for the
 *      next polling tick
 *   3. SURFACE the gap to the user / console so persistent gaps are
 *      visible rather than silent
 *
 * Pure functions — no Electron deps, no side effects. The BarDataService
 * calls `detectBarGaps` once after each fetch; consumers (LiveTradePanel)
 * call `gapStraddlesTimestamp` to decide whether the gap matters for a
 * specific trade.
 */

// How much larger than the expected period we tolerate before calling it
// a gap. 1.5× catches a single missing bar (delta = 2×period ≈ 2× > 1.5×)
// while ignoring the small clock jitter that can sit between consecutive
// bars (e.g., a 5m bar arriving with delta=301s instead of exactly 300).
export const GAP_TOLERANCE_FACTOR = 1.5;

/**
 * Detect time gaps in an OHLCV bars array.
 *
 * @param {Array<{ time:number }>} bars       — sorted ascending by `time` (Unix seconds).
 * @param {number} expectedPeriodSec          — the bar period (5m = 300, 15m = 900, ...).
 * @returns {Array<Gap>} — zero or more gaps, in order of appearance.
 *
 * Gap shape:
 *   {
 *     afterIndex:      number,   // index of bar just before the gap
 *     afterTs:         number,   // bars[afterIndex].time
 *     nextTs:          number,   // bars[afterIndex + 1].time
 *     delta:           number,   // nextTs - afterTs (always > expectedPeriodSec * tolerance)
 *     expectedPeriodSec, missingBarCount
 *   }
 *
 * Empty arrays / single-bar arrays / non-finite period return [].
 */
export const detectBarGaps = (bars, expectedPeriodSec) => {
  if (!Array.isArray(bars) || bars.length < 2) return [];
  if (!Number.isFinite(expectedPeriodSec) || expectedPeriodSec <= 0) return [];

  const threshold = expectedPeriodSec * GAP_TOLERANCE_FACTOR;
  const gaps = [];

  for (let i = 1; i < bars.length; i++) {
    const a = Number(bars[i - 1]?.time);
    const b = Number(bars[i]?.time);
    if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
    const delta = b - a;
    if (delta <= threshold) continue;

    // missingBarCount: how many bars would need to be there to fill the
    // gap at the expected period. delta=600 on a 300s period = 2 slots,
    // 1 of which is the next bar (already present) → 1 missing.
    // Floor + minus one — using round would over-report by one in cases
    // where the gap is between integer slots due to drift.
    const missingBarCount = Math.max(0, Math.floor(delta / expectedPeriodSec) - 1);

    gaps.push({
      afterIndex: i - 1,
      afterTs: a,
      nextTs: b,
      delta,
      expectedPeriodSec,
      missingBarCount,
    });
  }

  return gaps;
};

/**
 * Does a gap span the given timestamp? (Strict-inside semantics —
 * timestamps that exactly equal a bar boundary aren't "in" the gap.)
 *
 * Used by the live polling layer to ask "does any gap straddle my
 * trade's entry?" so we can target healing at the gaps that actually
 * matter (vs. session-break gaps in unrelated parts of the file).
 */
export const gapStraddlesTimestamp = (gap, ts) => {
  if (!gap || !Number.isFinite(ts)) return false;
  return ts > gap.afterTs && ts < gap.nextTs;
};

/**
 * Convenience — returns the FIRST gap that straddles `ts`, or null.
 * O(n) but n is typically <5 gaps per fetch so fine.
 */
export const findGapStraddling = (gaps, ts) => {
  if (!Array.isArray(gaps)) return null;
  for (const g of gaps) {
    if (gapStraddlesTimestamp(g, ts)) return g;
  }
  return null;
};

/**
 * Filter to "consequential" gaps — i.e. ones that span the futures
 * trading session rather than the 5pm–6pm ET daily closed hour or
 * weekend gaps. Used by the consumer when deciding what to warn about.
 *
 * Heuristic: a gap is consequential when its delta is at most
 * `maxConsequentialDeltaSec` (default 30 minutes). Longer gaps are
 * almost always session-break or weekend artifacts, not data dropouts.
 * Callers can tighten/loosen the threshold for their context.
 */
export const filterConsequentialGaps = (gaps, maxConsequentialDeltaSec = 30 * 60) => {
  if (!Array.isArray(gaps)) return [];
  return gaps.filter(g => g.delta <= maxConsequentialDeltaSec);
};
